// hooks/useBiometricCapture.js
import { useState, useCallback } from "react";
import BiometricService from "../services/BiometricService"; // adjust path

const useBiometricCapture = () => {
  const [showModal, setShowModal] = useState(false);
  const [biometricStatus, setBiometricStatus] = useState({
    loading: false,
    error: null,
    deviceInfo: null,
    captureData: null,
  });
  const [activeStep, setActiveStep] = useState(0);
  const [biometricProgress, setBiometricProgress] = useState({
    step: 0,
    message: "",
    percentage: 0,
  });

  const steps = ["Check Device", "Ready", "Capture", "Verifying"];

  const updateProgress = (step, message, percentage) => {
    setBiometricProgress({ step, message, percentage });
  };

  const checkDevice = useCallback(async () => {
    setBiometricStatus((prev) => ({ ...prev, loading: true, error: null }));
    setActiveStep(0);
    updateProgress(1, "Checking biometric device...", 10);

    try {
      const deviceCheck = await BiometricService.checkServiceStatus();

      if (!deviceCheck.serviceAvailable) {
        throw new Error(deviceCheck.error || "Biometric device not found");
      }

      updateProgress(2, "Device detected successfully", 30);
      setBiometricStatus((prev) => ({
        ...prev,
        deviceInfo: deviceCheck.deviceInfo,
        loading: false,
      }));
      setActiveStep(1);
    } catch (error) {
      setBiometricStatus((prev) => ({
        ...prev,
        loading: false,
        error: error.message,
      }));
    }
  }, []);

  const capture = useCallback(async (aadhaarNumber, wadh) => {
    setBiometricStatus((prev) => ({ ...prev, loading: true, error: null }));
    setActiveStep(2);
    updateProgress(3, "Ready to capture fingerprint...", 50);

    try {
      const captureResult = await BiometricService.captureBiometric({
        aadhaarNumber,
        wadh,
      });

      if (!captureResult.success) {
        throw new Error(captureResult.error || "Biometric capture failed");
      }

      updateProgress(4, "Fingerprint captured successfully", 80);
      setBiometricStatus((prev) => ({
        ...prev,
        loading: false,
        captureData: captureResult.data,
      }));
      setActiveStep(3);

      return captureResult.data;
    } catch (error) {
      setBiometricStatus((prev) => ({
        ...prev,
        loading: false,
        error: error.message,
      }));
      throw error;
    }
  }, []);

  const retry = useCallback(() => {
    setBiometricStatus((prev) => ({ ...prev, error: null, captureData: null }));
    setActiveStep(1);
  }, []);

  const reset = useCallback(() => {
    setBiometricStatus({
      loading: false,
      error: null,
      deviceInfo: null,
      captureData: null,
    });
    setBiometricProgress({ step: 0, message: "", percentage: 0 });
    setActiveStep(0);
  }, []);

  return {
    // State
    showModal,
    setShowModal,
    biometricStatus,
    activeStep,
    biometricProgress,
    steps,
    // Actions
    checkDevice,
    capture,
    retry,
    reset,
  };
};

export default useBiometricCapture;
